'use client';

import { useCallback, useEffect, useMemo, type MouseEvent as ReactMouseEvent } from 'react';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { AlertTriangle, Gem, LayoutGrid, Layers, MessageCircle, Wallet } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useAppModeConfig } from '@/lib/use-app-mode';
import { useMobileNav } from '@/providers/mobile-nav-provider';
import { useLaunchTransition } from '@/features/lobby/launch/LaunchTransitionProvider';
import { useProfileModal } from '@/contexts/profile-modal-context';
import { useWallet } from '@/hooks/use-wallet';
import { useLobbyHeaderStats } from '@/hooks/use-lobby-header-stats';
import { useLobbyBoards } from '@/hooks/use-lobby-boards';
import { useLobbySeatHoldings } from '@/hooks/use-lobby-seat-holdings';
import { useBoardsChatUnread } from '@/hooks/use-boards-chat-unread';
import { useUserProfile } from '@/hooks/use-user-profile';
import { selectMyBoards } from '@/features/lobby/panels/BoardDirectory.helpers';
import {
  BOARDROOM_BOARD_ID,
  getBoardroomLiveGridId,
} from '@/features/lobby/data/boardroom-grid';
import { AvatarWithFlair } from '@/components/prelaunch/avatar-with-flair';

/** Canonical mobile lobby URL — see `app/lobby/page.tsx` for the
 *  page that owns it. Lives outside the `(lobby)` route group so a
 *  navigation here never mounts `LobbyClient`, which is the whole
 *  point of the route: the Lobby tab should never load a board. */
const LOBBY_PATH = '/lobby';

/** Legacy `?mode=lobby` sentinel still consumed by `LobbyClient` for
 *  any URL that lands inside the `(lobby)` route group with the
 *  param set (shared links, deep-links from older builds). New
 *  navigations from this nav use `LOBBY_PATH` instead, so the
 *  param is only read here for highlighting (so the Lobby tab
 *  still looks active when a viewer is on `/?mode=lobby` or
 *  `/board/:id?mode=lobby` from a legacy link). Must match
 *  `LobbyClient`'s constant of the same name. */
const LOBBY_MODE_PARAM = 'mode';
const LOBBY_MODE_VALUE = 'lobby';

/**
 * `AppBottomNav` — unified mobile bottom navigation. Five tabs:
 *
 *   1. **Lobby** — routes to `/lobby`, the dedicated lightweight
 *      board directory (see `app/lobby/page.tsx`). The page lives
 *      outside the `(lobby)` route group on purpose: navigating
 *      there never mounts `LobbyClient`, never boots the Three.js
 *      canvas, and never warms any per-board state — the directory
 *      IS the destination. From `/chat` (or any other non-lobby
 *      route) this means a single hop to a fresh, board-free list,
 *      not a hop to "the lobby on top of the last board you were
 *      on" (the previous `?mode=lobby` design — kept around for
 *      backwards-compat with shared links but no longer the path
 *      this nav writes). Tapping Lobby while already on `/lobby`
 *      (or on a legacy `?mode=lobby` URL) is a no-op so the tap
 *      feels instant; there is intentionally no second-tap "pop
 *      back to the canvas" toggle, which previously turned the tab
 *      into a Lobby↔Boardroom switch and made tapping it on the
 *      mobile homepage (`/` defaults to implicit lobby mode in
 *      `LobbyClient`) reveal the Three.js Boardroom canvas — the
 *      hundreds of ms of WebGL initialisation that the viewer
 *      naturally reads as "Lobby is slow". To return to a specific
 *      board the viewer either taps its card in the directory or
 *      taps its chip in `MobileBoardRail` (the joined-boards rail
 *      in the top chrome). The lobby URL is also prefetched on
 *      mount so a cold tap from `/chat` lands on warm chunks.
 *   2. **My Tiles** — routes to `/tiles`, the cross-board tile
 *      management view. Replaces the previous "+ Board" slot — board
 *      creation is now surfaced inside the Profile modal so the
 *      bottom nav stays focused on stewardship of existing tiles
 *      (the operation users return for repeatedly) instead of one-off
 *      creation. The badge counts seats that are within
 *      `RUNWAY_HEALTHY` of forfeit so users can see at a glance
 *      whether the page warrants a tap. When the viewer holds zero
 *      tiles the slot stays present but inert; tapping still routes
 *      to the empty-state on `/tiles` so the UI is never a dead
 *      affordance.
 *   3. **Chat** — routes to `/chat` (the board-list view, mirroring
 *      desktop "My Boards"). Carries an aggregate red-dot when any of
 *      the viewer's owned boards has unread chat. Sits to the LEFT of
 *      Profile so the connect/profile slot stays at the rightmost
 *      thumb-friendly anchor.
 *   4. **Profile** — opens the in-app `ProfileModal` for the connected
 *      wallet (gated on `showPortfolio`). When connected, the trigger
 *      shows the viewer's `AvatarWithFlair` (square avatar with level-
 *      flair ring) instead of a generic icon + "Profile" label so the
 *      bottom nav reads as personalised at a glance. A claim-available
 *      Gem badge stacks on the avatar's bottom-right when there's
 *      something to claim. The Tutorial entry that previously
 *      occupied its own slot has moved inside the Profile modal's
 *      mobile Wallet section to free the space. The mobile
 *      notifications dropdown lives in `LobbyFooter` (the "Alerts"
 *      pill next to SFX) — see `NotificationsDropdown`'s
 *      `'footer-pill'` variant. Disconnected state keeps the
 *      `Wallet`-icon "Connect" affordance.
 *
 * The nav respects `useMobileNav().hidden` so drawers/sheets can slide
 * it out of the way. Only renders below `lg`.
 */
export function AppBottomNav({ className }: { className?: string }) {
  const pathname = usePathname();
  const router = useRouter();
  const searchParams = useSearchParams();
  const modeConfig = useAppModeConfig();
  const { hidden } = useMobileNav();
  // `useLaunchTransition` is still imported because the desktop entry
  // for board creation lives in the Profile modal which previously
  // didn't have it; the hook is provider-scoped and a no-op outside
  // its provider, so leaving it here is harmless.
  useLaunchTransition();
  const { openProfile } = useProfileModal();
  const {
    address: walletAddress,
    connectWallet,
    connectBusy,
    ready: walletReady,
  } = useWallet();
  // Lifts the Gem-shaped badge that already drives the desktop
  // RewardsSheet pill onto the mobile Profile tab. Same source of truth
  // (`useLobbyHeaderStats`) means a claim that's surfaced anywhere in
  // the app shows up here too — no extra fetch on mount and no
  // divergence between desktop/mobile claim signals. The pill stays
  // hidden when there's nothing to claim, so it doesn't compete for
  // attention with the rest of the bottom nav.
  const { hasAnyClaimable } = useLobbyHeaderStats();

  // Avatar / flair level for the Profile slot — driven by the same
  // `useUserProfile` hook the `ProfileHud` uses (custom > farcaster >
  // twitter > ens > generated, with a level fallback of `0`). When
  // disconnected we don't fetch (`useUserProfile` short-circuits on
  // `null` address), and the disconnected branch swaps in the
  // Wallet-icon Connect button instead of trying to render a flair
  // ring around an empty avatar.
  const { avatarUrl, level } = useUserProfile(walletAddress ?? null);

  // Aggregate "any of my boards has unread chat" signal for the
  // bottom-nav Chat tab dot. Mirrors the per-row My Boards rule: held
  // tiles OR creator. The hook polls every 30s (same cadence as the
  // chat-sheet bell + lobby My Boards dots) and short-circuits to
  // `false` when there's no wallet connected.
  const { pinned, curated, community } = useLobbyBoards();
  const { holdings } = useLobbySeatHoldings();
  const viewerLower = walletAddress ? walletAddress.toLowerCase() : null;
  const myBoardRoomIds = useMemo(() => {
    if (!viewerLower) return [] as string[];
    const boardroomLiveId = getBoardroomLiveGridId();
    const heldIds = new Set<string>();
    for (const h of holdings) {
      const id = h.boardId.toLowerCase();
      if (boardroomLiveId && id === boardroomLiveId.toLowerCase()) {
        heldIds.add(BOARDROOM_BOARD_ID);
      } else {
        heldIds.add(id);
      }
    }
    const allLive = [...pinned, ...curated, ...community];
    return selectMyBoards(allLive, heldIds, viewerLower).map((b) => b.id);
  }, [pinned, curated, community, holdings, viewerLower]);
  const { hasUnread: hasUnreadInRoom } = useBoardsChatUnread(myBoardRoomIds);
  const chatHasUnread = useMemo(
    () => myBoardRoomIds.some((id) => hasUnreadInRoom(id)),
    [myBoardRoomIds, hasUnreadInRoom],
  );

  const isBoardRoute = pathname === '/' || pathname.startsWith('/board');

  // Warm the lobby route's chunks on mount so a cold tap from
  // `/chat` or `/create` lands on a page that's already in cache.
  // `router.prefetch` is internally de-duped and a no-op once the
  // route is compiled, so the cost is bounded — we just want the
  // network round-trip to happen during idle time, not at tap.
  // Same pattern `LaunchTransitionProvider` uses to make `/create`
  // feel instant; the lobby page deserves the same treatment now
  // that it's a real, separate route. No board-route gate (we
  // prefetch even from `/lobby` itself) because Next.js de-dupes
  // the call and the page chunk is small.
  useEffect(() => {
    try {
      router.prefetch(LOBBY_PATH);
    } catch {
      // Prefetch is best-effort — failing is fine, the navigation
      // will just fall back to a cold route load.
    }
  }, [router]);

  const isLobbyMode = useMemo(() => {
    // The Lobby tab highlights when the viewer is on the dedicated
    // `/lobby` page (the canonical destination this nav now routes
    // to) OR on a legacy URL that still triggers `LobbyClient`'s
    // in-place lobby mode — i.e. `/?mode=lobby`, `/board/:id?mode=
    // lobby`, or the mobile homepage's implicit lobby default
    // (`/` with no `?mode`). Including the legacy paths keeps the
    // tab feeling "active" for users who hit a shared link or
    // bookmarked an older URL, and pairs with the no-op
    // short-circuit in `onLobby` so tapping the tab while already
    // viewing any flavour of lobby is genuinely free.
    if (pathname === LOBBY_PATH) return true;
    const explicit = (searchParams.get(LOBBY_MODE_PARAM) ?? '').toLowerCase();
    if (explicit === LOBBY_MODE_VALUE) return isBoardRoute;
    if (explicit === '' && pathname === '/') return true;
    return false;
  }, [isBoardRoute, searchParams, pathname]);

  const onLobby = useCallback(() => {
    // Already showing the lobby directory (any flavour — `/lobby`,
    // legacy `?mode=lobby`, or the mobile homepage default)? No-op
    // so the tap feels instant. No `router.push`, no re-render
    // churn, no chance of a navigation stalling on a search-param
    // diff or remounting `LobbyDirectoryClient` for nothing.
    if (isLobbyMode) return;

    // Otherwise route to the canonical lobby URL. `/lobby` lives
    // outside the `(lobby)` route group, so this navigation never
    // mounts `LobbyClient` and never loads any board state — which
    // is the entire point of the route. Compare with the previous
    // design which pushed the last-visited board path with
    // `?mode=lobby` and ate the full `LobbyClient` mount cost (and
    // its hidden Three.js canvas) every time the viewer landed
    // here from `/chat`.
    router.push(LOBBY_PATH);
  }, [isLobbyMode, router]);

  // Tile risk badge — counts seats whose forfeit window is within the
  // app's "needs attention" runway. Surfaces on the My Tiles tab so
  // users notice degrading positions without needing to tap into the
  // page first. Re-uses the same `holdings` payload the chat-unread
  // dot already reads, so no extra fetch.
  const tilesRisk = useMemo(() => {
    let danger = 0;
    let warn = 0;
    for (const h of holdings) {
      for (const s of h.seats) {
        if (s.lockRisk === 'danger') danger += 1;
        else if (s.lockRisk === 'warn') warn += 1;
      }
    }
    return { danger, warn };
  }, [holdings]);
  const isTilesRoute = pathname === '/tiles' || (pathname?.startsWith('/tiles/') ?? false);
  // Prefetch the tiles route the same way we prefetch `/lobby` — keeps
  // a cold tap from `/chat` or `/create` warm.
  useEffect(() => {
    try {
      router.prefetch('/tiles');
    } catch {
      // best-effort prefetch
    }
  }, [router]);
  const onTiles = useCallback(
    (_event: ReactMouseEvent<HTMLButtonElement>) => {
      if (isTilesRoute) return;
      router.push('/tiles');
    },
    [isTilesRoute, router],
  );

  // Chat lives at `/chat`. The route renders the board-list view when
  // no `?board=` is set and the chat-only view when it is — both
  // belong to this nav slot. We treat any `/chat*` path as "active"
  // so drilling into a specific room keeps the tab highlighted.
  const isChatRoute = pathname === '/chat' || (pathname?.startsWith('/chat/') ?? false);
  const onChat = useCallback(() => {
    // Always land on the list view (no `?board=` param) so the tab
    // behaves as a "back to my chat rooms" affordance. The list seeds
    // the user's last room via `useUserProfile`-scoped sessionStorage
    // if we ever decide to deep-link straight in.
    if (pathname === '/chat' && !searchParams.get('board')) return;
    router.push('/chat');
  }, [pathname, router, searchParams]);

  return (
    <nav
      className={cn(
        'lg:hidden border-t border-amber-500/25 bg-gradient-to-t from-black/95 via-black/85 to-black/70 backdrop-blur-md safe-bottom transition-transform duration-300 ease-in-out',
        hidden && 'translate-y-full',
        className,
      )}
      aria-label="Main navigation"
    >
      <ul className="flex items-stretch justify-around h-14">
        <li className="flex-1">
          <button
            type="button"
            onClick={onLobby}
            aria-current={isLobbyMode ? 'page' : undefined}
            aria-pressed={isLobbyMode}
            className={cn(
              'group flex h-full w-full flex-col items-center justify-center gap-0.5 transition-colors',
              isLobbyMode
                ? 'text-amber-100'
                : 'text-zinc-300 hover:text-amber-100',
            )}
          >
            <LayoutGrid className="h-4 w-4" />
            <span className="text-[9px] font-mono uppercase tracking-[0.2em]">
              Lobby
            </span>
          </button>
        </li>

        <li className="flex-1">
          <button
            type="button"
            onClick={onTiles}
            aria-current={isTilesRoute ? 'page' : undefined}
            aria-pressed={isTilesRoute}
            aria-label={
              tilesRisk.danger > 0
                ? `My Tiles (${tilesRisk.danger} at risk)`
                : 'My Tiles'
            }
            className={cn(
              'relative group flex h-full w-full flex-col items-center justify-center gap-0.5 transition-colors',
              isTilesRoute
                ? 'text-amber-100'
                : 'text-zinc-300 hover:text-amber-100',
            )}
          >
            <span className="relative">
              <Layers className="h-4 w-4" />
              {tilesRisk.danger > 0 ? (
                <span
                  aria-hidden
                  className="pointer-events-none absolute -top-1 -right-1.5 inline-flex h-3.5 min-w-[14px] px-0.5 items-center justify-center rounded-full bg-red-500 text-[8px] font-bold text-white ring-1 ring-red-300/60 tabular-nums"
                >
                  {tilesRisk.danger > 9 ? '9+' : tilesRisk.danger}
                </span>
              ) : tilesRisk.warn > 0 ? (
                <AlertTriangle
                  aria-hidden
                  className="pointer-events-none absolute -top-1 -right-1.5 h-3 w-3 text-orange-300"
                />
              ) : null}
            </span>
            <span className="text-[9px] font-mono uppercase tracking-[0.2em]">
              Tiles
            </span>
          </button>
        </li>

        <li className="flex-1">
          <button
            type="button"
            onClick={onChat}
            aria-current={isChatRoute ? 'page' : undefined}
            aria-pressed={isChatRoute}
            aria-label={
              chatHasUnread ? 'Open chat (unread messages)' : 'Open chat'
            }
            className={cn(
              'relative group flex h-full w-full flex-col items-center justify-center gap-0.5 transition-colors',
              isChatRoute
                ? 'text-amber-100'
                : 'text-zinc-300 hover:text-amber-100',
            )}
          >
            <span className="relative">
              <MessageCircle className="h-4 w-4" />
              {chatHasUnread && (
                <span
                  aria-hidden
                  className="pointer-events-none absolute -top-1 -right-1.5 h-2 w-2 rounded-full bg-brand-red shadow-[0_0_4px_rgba(239,68,68,0.6)]"
                />
              )}
            </span>
            <span className="text-[9px] font-mono uppercase tracking-[0.2em]">
              Chat
            </span>
          </button>
        </li>

        {modeConfig.showPortfolio && (
          <li className="flex-1">
            {walletAddress ? (
              // When connected, the Profile slot shows the viewer's
              // square AvatarWithFlair (level-driven border) instead
              // of the generic User icon + "Profile" label. The only
              // overlay is the claim-available Gem at the avatar's
              // bottom-right; notifications now live in the
              // LobbyFooter "Alerts" pill (see
              // `NotificationsDropdown` with `variant="footer-pill"`)
              // so the avatar reads as a clean identity surface.
              // The text label is intentionally absent — the flair
              // ring already encodes a strong identity signal,
              // matching how the avatar replaces text-name
              // affordances in the desktop header.
              <button
                type="button"
                onClick={() => openProfile(walletAddress)}
                className="relative group flex h-full w-full flex-col items-center justify-center gap-0.5 text-zinc-300 hover:text-amber-100 transition-colors"
                aria-label={
                  hasAnyClaimable
                    ? 'Open my profile (claim available)'
                    : 'Open my profile'
                }
                title="Open my profile"
              >
                <span className="relative inline-flex">
                  <AvatarWithFlair
                    avatarUrl={avatarUrl}
                    address={walletAddress}
                    level={level}
                    size="xs"
                  />
                  {hasAnyClaimable && (
                    <span
                      className="pointer-events-none absolute -top-1 -right-1.5 inline-flex h-3.5 w-3.5 items-center justify-center rounded-full bg-amber-400 text-black ring-1 ring-amber-200/70 shadow-[0_0_0_1px_rgba(0,0,0,0.7)]"
                      aria-hidden
                    >
                      <Gem className="h-2 w-2" strokeWidth={2.5} />
                    </span>
                  )}
                </span>
              </button>
            ) : (
              // Disconnected: swap the Profile tab for a Connect entry
              // that opens the Privy auth modal directly. Without this
              // mobile users had no entry point to the wallet flow —
              // the disabled `Profile` tab read as a dead-end and the
              // only working Connect surface was the chat gate banner
              // or per-action drawers (which require a tap on a tile
              // first). Using the same `connectWallet` the desktop
              // header uses (`@/hooks/use-wallet`) keeps the auth
              // experience identical across breakpoints.
              <button
                type="button"
                onClick={() => connectWallet()}
                disabled={connectBusy || !walletReady}
                className={cn(
                  'relative group flex h-full w-full flex-col items-center justify-center gap-0.5 transition-colors',
                  connectBusy || !walletReady
                    ? 'text-zinc-500 cursor-wait'
                    : 'text-amber-100 hover:text-amber-50',
                )}
                aria-label="Connect a wallet"
                title="Connect a wallet"
              >
                <Wallet className="h-4 w-4" />
                <span className="text-[9px] font-mono uppercase tracking-[0.2em]">
                  Connect
                </span>
              </button>
            )}
          </li>
        )}
      </ul>
    </nav>
  );
}
